import { FeedbackSignalRGate, SignalRStatus } from '@/entities/feedbacks'
import { ROUTES, STRINGS } from '@/shared/config'
import { ContentWrapper, ErrorResult, HeaderContent } from '@/shared/ui'
import { FilterOutlined, SearchOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Flex,
  Input,
  Layout,
  Popover,
  Splitter,
  Typography,
} from 'antd'
import { useGate, useUnit } from 'effector-react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  $feedbacksData,
  $feedbacksHasNextPage,
  $feedbacksSearchInputValue,
  FeedbacksPageGate,
  feedbacksQuery,
  feedbacksReachedEnd,
  feedbacksSearchChanged,
} from './model/feedbacks-list.model'
import styles from './styles.module.scss'
import { CardHeader } from './ui/CardHeader'
import { FeedbackChat } from './ui/FeedbackChat/'
import { FeedbackChatList } from './ui/FeedbackChatList'
import { FeedbackFilter } from './ui/FeedbackFilter'

export function FeedbackPage() {
  const { id: chatId } = useParams<{ id?: string }>()
  const navigate = useNavigate()

  useGate(FeedbacksPageGate)
  useGate(FeedbackSignalRGate)
  const [
    feedbackData,
    loading,
    error,
    searchValue,
    handleSearchChange,
    handleNextPage,
    hasNextPage,
  ] = useUnit([
    $feedbacksData,
    feedbacksQuery.$pending,
    feedbacksQuery.$error,
    $feedbacksSearchInputValue,
    feedbacksSearchChanged,
    feedbacksReachedEnd,
    $feedbacksHasNextPage,
  ])

  return (
    <Layout className={styles.layout}>
      <HeaderContent
        title="Обратная связь"
        description="Здесь можно просматривать вопросы и предложения пользолвателей, а также отвечать на них"
      />
      {error ? (
        <ContentWrapper>
          <ErrorResult error={error} />
        </ContentWrapper>
      ) : (
        <Card
          className={styles.card}
          classNames={{
            body: styles.cardBody,
          }}
        >
          <Splitter className={styles.splitter}>
            <Splitter.Panel
              defaultSize="37%"
              min="20%"
              max="60%"
              className={styles.splitterPanel}
            >
              <CardHeader>
                <Flex gap={8} align="center">
                  <Typography.Text strong>Все обращения</Typography.Text>
                  <SignalRStatus />
                </Flex>
              </CardHeader>
              <Flex gap="small" className={styles.searchWrapper}>
                <Input
                  placeholder="Поиск по имени пользователя"
                  allowClear
                  addonAfter={<SearchOutlined />}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const searchQueryString = event.target.value
                    handleSearchChange(searchQueryString)
                  }}
                  className={styles.searchInput}
                  value={searchValue ?? ''}
                />
                <Popover
                  content={<FeedbackFilter />}
                  title={STRINGS.label_filters_control}
                  trigger="click"
                  placement="bottom"
                  destroyOnHidden
                >
                  <Button icon={<FilterOutlined />} />
                </Popover>
              </Flex>

              <FeedbackChatList
                data={feedbackData}
                selectedChatId={chatId}
                loading={loading}
                onClick={(id) =>
                  void navigate(ROUTES.FEEDBACK.CHAT.replace(':id?', id))
                }
                hasNextPage={hasNextPage}
                onNextPage={handleNextPage}
              />
            </Splitter.Panel>
            <Splitter.Panel className={styles.splitterPanel}>
              {chatId ? (
                <FeedbackChat id={chatId} />
              ) : (
                <Flex vertical flex={1}>
                  <CardHeader />
                  <Flex align="center" justify="center" flex={1}>
                    <Typography.Text className={styles.emptyChatText}>
                      Выберите, кому хотели бы написать
                    </Typography.Text>
                  </Flex>
                </Flex>
              )}
            </Splitter.Panel>
          </Splitter>
        </Card>
      )}
    </Layout>
  )
}
